<template>
  <v-data-table
    class="pages-table"
    flat
    :headers="headers"
    :items="items"
    disable-sorting
    :hide-default-footer="items.length <= 10"
  >
    <template v-slot:top>
      <v-dialog v-model="dialog" max-width="500">
        <template v-slot:activator="{ on }">
          <v-btn
            color="success"
            dark
            class="mb-2 mx-4"
            v-on="on"
            depressed
            small
            >New page</v-btn
          >
        </template>
        <v-card>
          <v-card-title>
            <span class="title">{{ formTitle }}</span>
          </v-card-title>

          <v-form v-model="drawer.valid">
            <v-card-text>
              <component
                v-for="(field, index) in formFields"
                :is="`${field.type}-field`"
                :key="index"
                :field="field"
                :value.sync="formData[index]"
              ></component>
            </v-card-text>
          </v-form>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              text
              :disabled="!drawer.valid"
              :loading="drawer.loading"
              @click="save"
              >Save</v-btn
            >

            <v-btn
              color="error"
              text
              :loading="drawer.loading"
              @click="closeDialog"
              >Close</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <template v-slot:item.name="{ item }">{{
      truncateString(item.name, 30)
    }}</template>

    <template v-slot:item.controls="{ item }">
      <v-tooltip bottom v-if="item.home">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon small color="success" v-if="item.home">
            <v-icon>mdi-home-outline</v-icon>
          </v-btn>
        </template>
        <span>This page is currently set as the homepage</span>
      </v-tooltip>

      <v-tooltip bottom v-else>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon small @click="setHomePage(item.id)">
            <v-icon>mdi-home-outline</v-icon>
          </v-btn>
        </template>
        <span>Set page as the homepage</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon small @click="togglePageMenuItem(item)">
            <v-icon v-if="item.menu_item">mdi-eye-outline</v-icon>
            <v-icon v-else>mdi-eye-off-outline</v-icon>
          </v-btn>
        </template>
        <span v-if="item.menu_item">Hide page on the menu</span>
        <span v-else>Show page on the menu</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon small @click="copyItem(item.id)">
            <v-icon size="20">mdi-content-copy</v-icon>
          </v-btn>
        </template>
        <span>Create a copy of this page</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon small color="accent" @click="editItem(item)">
            <v-icon
              :fill="$vuetify.theme.defaults.dark.accent"
              style="width: 18"
              >$edit</v-icon
            >
          </v-btn>
        </template>
        <span>Edit page</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            icon
            small
            color="error"
            @click="deleteItem(item.id)"
            :disabled="parseBoolean(item.home)"
          >
            <v-icon>mdi-trash-can-outline</v-icon>
          </v-btn>
        </template>
        <span>Delete page</span>
      </v-tooltip>
    </template>
  </v-data-table>
</template>

<script>
import {
  setHomePage,
  upsertPage,
  deletePage,
  copyPage,
  setPageMenuItem
} from '@/graphql/page.gql'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'PageNavigation',
  props: {
    templateID: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true
    }
  },
  data: () => ({
    dialog: false,
    headers: [
      {
        text: 'Name',
        value: 'name'
      },
      {
        sortable: false,
        align: 'right',
        text: 'Controls',
        value: 'controls'
      }
    ],
    formFields: {
      name: {
        label: 'Name',
        type: 'text',
        rules: ['required']
      },
      slug: {
        label: 'Slug',
        type: 'text',
        rules: ['required'],
        disabled: true
      }
    },
    formData: {
      id: null,
      name: '',
      slug: ''
    },
    defaultFormData: {
      id: null,
      name: '',
      slug: ''
    }
  }),
  computed: {
    formTitle() {
      return this.formData.id === null ? 'New page' : 'Edit page'
    },
    ...mapState('editor', ['drawer'])
  },
  watch: {
    dialog(value) {
      value || this.closeDialog()
    },
    'formData.name': function(value) {
      this.formData.slug = this.slugify(value)
    }
  },
  methods: {
    ...mapActions('editor', ['saveChanges', 'confirmAndSaveChanges']),
    editItem(data) {
      this.formData = Object.assign(
        {},
        {
          id: data.id,
          name: data.name,
          slug: data.slug
        }
      )

      this.dialog = true
    },
    setHomePage(id) {
      this.saveChanges({
        mutation: setHomePage,
        variables: {
          id
        },
        messages: { success: 'Home page successfully updated' },
        refetch: true,
        close: false
      })
    },
    togglePageMenuItem(item) {
      this.saveChanges({
        mutation: setPageMenuItem,
        variables: { id: item.id, menu_item: !item.menu_item },
        messages: {
          success: item.menu_item
            ? 'Page successfully removed from menu.'
            : 'Page successfully added to menu.'
        },
        refetch: true,
        close: false
      })
    },
    deleteItem(id) {
      // TODO - fix redirects when current page is deleted.
      this.confirmAndSaveChanges({
        mutation: deletePage,
        variables: { id },
        messages: {
          confirm: 'Sections assigned to this page will be deleted as well.',
          success: 'Page successfully deleted.'
        },
        refetch: true,
        close: false
      })
    },
    copyItem(id) {
      this.saveChanges({
        mutation: copyPage,
        variables: { id },
        messages: {
          success: 'Copy successfully created.'
        },
        refetch: true,
        close: false
      })
    },
    save() {
      this.saveChanges({
        mutation: upsertPage,
        variables: {
          data: {
            ...this.formData,
            template_id: this.templateID
          }
        },
        messages: {
          success: this.formData.id
            ? 'Page successfully updated.'
            : 'Page successfully created.'
        },
        refetch: true,
        close: false
      })
    },
    closeDialog() {
      this.dialog = false
      this.formData = Object.assign({}, this.defaultFormData)
    }
  }
}
</script>
